import { FC, MouseEvent, useState, useEffect } from 'react'
import { Button, Popover, TextField, InputAdornment } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import {
  DatePickerWrapper,
  StyledDateCalendar,
  StyledDatePickerWrapper,
  ActionButtons
} from './singleDatePicker.styles'
import { CalendarIcon } from '../svg'
import theme from 'theme'

interface Props {
  value?: Dayjs | null
  onChange?: (value: Dayjs | null) => void
  label?: string
  wrapperWidth?: string
  inputWidth?: string
  minWidth?: string
  minDate?: Dayjs
  maxDate?: Dayjs
}

const SingleDatePicker: FC<Props> = ({
  value,
  onChange,
  label = 'Date',
  wrapperWidth,
  inputWidth,
  minWidth,
  minDate,
  maxDate
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(value || null)
  const [tempDate, setTempDate] = useState<Dayjs | null>(selectedDate)

  // Sync internal state when value prop changes (e.g., from prefilled form data)
  useEffect(() => {
    setSelectedDate(value || null)
  }, [value])

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setTempDate(selectedDate)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOk = () => {
    setSelectedDate(tempDate)
    onChange?.(tempDate)
    handleClose()
  }

  const handleCancel = () => {
    setTempDate(selectedDate)
    handleClose()
  }

  const formatDate = () => {
    if (selectedDate) {
      return selectedDate.format('DD/MM/YYYY')
    }
    return 'Select date'
  }

  const open = Boolean(anchorEl)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePickerWrapper className="date-picker-wrapper" sx={{ width: wrapperWidth ?? undefined }}>
        <TextField
          label={label}
          onClick={handleClick}
          className={`date-picker-input ${open ? 'date-picker-input--open' : ''}`}
          sx={{ width: inputWidth ?? undefined, minWidth }}
          variant="outlined"
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarIcon />
              </InputAdornment>
            )
          }}
          value={formatDate()}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          sx={{
            marginTop: '8px',

            '.MuiPaper-root': {
              boxShadow: theme.colors.tooltipShadow,
              borderRadius: '12px',
              overflow: 'visible',

              '& > .MuiBox-root:first-of-type': {
                backgroundColor: `${theme.colors.calendarBg} !important`
              }
            }
          }}
        >
          <StyledDatePickerWrapper>
            <StyledDateCalendar
              value={tempDate}
              onChange={(newValue) => setTempDate(newValue as Dayjs | null)}
              minDate={minDate}
              maxDate={maxDate}
            />

            <ActionButtons>
              <Button onClick={handleCancel} className="cancel-button">
                Cancel
              </Button>
              <Button onClick={handleOk} className="ok-button">
                Ok
              </Button>
            </ActionButtons>
          </StyledDatePickerWrapper>
        </Popover>
      </DatePickerWrapper>
    </LocalizationProvider>
  )
}

export default SingleDatePicker
